class LuissSearch {

  constructor(credentials, excludeFields = [], notFoundMessages = [], pagination = [], sortBy = null, filterBy = null, groupBy = null, queryBy = 'title, content', exhaustiveSearch = true) {
    this.credentials = credentials;
    this.excludeFields = excludeFields;
    this.notFoundMessages = notFoundMessages;
    this.pagination = pagination;
    this.sortBy = sortBy;
    this.filterBy = filterBy;
    this.groupBy = groupBy;
    this.queryBy = queryBy;
    this.exhaustiveSearch = exhaustiveSearch;
    this.isDefaultView = false;
  }

  /**
   * Helper to check if any refinements (filters or query) are active.
   *
   * @param {object} helper - The instantsearch helper instance.
   *
   * @returns {boolean} True if there are active refined attributes or a non-empty query.
   */
  hasActiveRefinements(helper) {
    if (!helper || !helper.state) {
      return false;
    }

    const { state } = helper;

    // Check for refined attributes (filters)
    const hasFilters = Object.values(state.disjunctiveFacetsRefinements || {}).some(v => v.length > 0) ||
                       Object.values(state.facetsRefinements || {}).some(v => v.length > 0) ||
                       Object.values(state.hierarchicalFacetsRefinements || {}).some(v => v.length > 0) ||
                       Object.values(state.numericRefinements || {}).some(v => Object.keys(v).length > 0);

    // Check for non-empty query
    const hasQuery = state.query && state.query.trim() !== '';

    return !!(hasFilters || hasQuery);
  }

  /**
   * Determines whether post-search event callbacks should be executed based on changes in the search query or filters.
   *
   * @param {object} results - The current search results object from InstantSearch.js.
   * @param {object} widgetParams - The parameters object for the widget, used to store the last query and filters for comparison.
   *
   * @returns {boolean} True if callbacks should be run (i.e., if the query or filters have changed since the last render), false otherwise.
   */
  shouldRunCallbacks(results, widgetParams) {
    const currentQuery = results && typeof results.query === 'string' ? results.query : '';
    const currentFilters = results && results._state
      ? JSON.stringify({
        disjunctiveFacetsRefinements: results._state.disjunctiveFacetsRefinements || {},
        facetsRefinements: results._state.facetsRefinements || {},
        hierarchicalFacetsRefinements: results._state.hierarchicalFacetsRefinements || {},
        numericRefinements: results._state.numericRefinements || {},
        tagRefinements: results._state.tagRefinements || [],
        filters: results._state.filters || '',
      })
      : '';

    const lastQuery = widgetParams.lastQuery ?? null;
    const lastFilters = widgetParams.lastFilters ?? null;
    const shouldRunCallbacks = currentQuery !== lastQuery || currentFilters !== lastFilters;

    widgetParams.lastQuery = currentQuery;
    widgetParams.lastFilters = currentFilters;

    return shouldRunCallbacks;
  }

  /**
   * Initializes and returns an InstantSearch.js search client connected to Typesense.
   *
   * Stores `additionalSearchParameters` as a class property so it can be
   * mutated after construction (e.g. by `setPinnedHits`) and the change is
   * picked up by the TypesenseInstantSearchAdapter on the next request, because
   * the adapter holds the same object reference.
   *
   * @param {string} indexName - The name of the Typesense index to search.
   * @param {object} [options] - Optional configuration.
   * @param {Function} [options.searchFunction] - InstantSearch.js `searchFunction`
   *   hook, called before every search request. Receives the algoliasearch
   *   helper instance. Must call `helper.search()` to proceed. Useful for
   *   dynamically mutating `additionalSearchParameters` (e.g. toggling
   *   `pinned_hits`) before the request is sent.
   * @param {object} [options.urlAliases] - Map of Typesense attribute name to
   *   the public name it is exposed as in the URL, e.g.
   *   `{ field_news_category: 'category' }`. Attributes with no entry keep
   *   their raw name. Aliases must be unique within an index, otherwise two
   *   attributes collapse onto the same URL parameter. Read only when
   *   `maskUrls` is true.
   * @param {boolean} [options.maskUrls] - When true, internal identifiers are
   *   kept out of the URL: the query string carries the bare index name and
   *   the public filter aliases. When false, the default InstantSearch
   *   mapping writes the prefixed collection name and the raw attribute
   *   names, the format in use before the masking. Follows the
   *   SEARCH_URL_MASKING environment flag through drupalSettings.
   *
   * @returns {object} An InstantSearch.js search client instance.
   */
  getSearchClient(indexName, options = {}) {
    this.additionalSearchParameters = {
      exclude_fields: 'embedding,chunk,' + this.excludeFields.join(','),
      exhaustive_search: this.exhaustiveSearch,
      query_by: this.queryBy,
      use_cache: true,
    };
    if (this.sortBy) {
      this.additionalSearchParameters.sort_by = this.sortBy;
    }
    if (this.groupBy) {
      this.additionalSearchParameters.group_by = this.groupBy;
      this.additionalSearchParameters.group_limit = 1;
    }

    const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey: this.credentials.apiKey,
        nodes: [
          {
            host: this.credentials.host, path: '', port: this.credentials.port, protocol: this.credentials.protocol,
          },
        ],
        cacheSearchResultsForSeconds: 2 * 60,
        useServerSideSearchCache: true,
      },
      additionalSearchParameters: this.additionalSearchParameters,
    });

    const collectionName = this.credentials.collection_prefix + indexName;

    // URL masking, applied by the stateMapping below. Typesense attribute
    // names (field_news_category) are internal, so the URL exposes the public
    // alias configured per filter (category) and the bare index name instead
    // of the prefixed collection name.
    const maskUrls = Boolean(options.maskUrls);
    const aliases = options.urlAliases || {};
    const attributesByAlias = Object.fromEntries(
      Object.entries(aliases).map(([attribute, alias]) => [alias, attribute])
    );
    // uiState keys whose own keys are attribute names. Every other key
    // (query, page, configure) is passed through untouched.
    const attributeKeyedStateKeys = [
      'refinementList',
      'menu',
      'toggle',
      'hierarchicalMenu',
      'numericMenu',
      'range',
    ];
    const renameAttributes = (indexUiState, map) => Object.fromEntries(
      Object.entries(indexUiState).map(([stateKey, value]) => {
        if (!attributeKeyedStateKeys.includes(stateKey) || value === null || typeof value !== 'object') {
          return [stateKey, value];
        }
        return [stateKey, Object.fromEntries(
          Object.entries(value).map(([attribute, refinement]) => [map[attribute] || attribute, refinement])
        )];
      })
    );

    const instantsearchOptions = {
      searchClient: typesenseInstantsearchAdapter.searchClient,
      indexName: collectionName,
      // Custom routing replaces the default `routing: true` for two reasons:
      //
      // 1. Human-readable URLs — the default qs encoder aggressively
      //    percent-encodes brackets, spaces, and Unicode, producing URLs
      //    like `?query%5B0%5D=ciao%20mondo`. The custom encoder below
      //    only escapes the five characters that would actually break
      //    query-string parsing (% & = + # ?), keeping everything else
      //    as-is so URLs stay clean and shareable.
      //
      // 2. High arrayLimit — qs defaults to an arrayLimit of 20, meaning
      //    indexed arrays with more than 20 entries (e.g. `facet[21]=val`)
      //    are parsed as plain objects instead of arrays, which breaks
      //    facet restoration from the URL. Setting arrayLimit to 100
      //    ensures users with many active facet selections can bookmark
      //    or share links without losing filters on page reload.
      routing: {
        router: instantsearch.routers.history({
          createURL({ qsModule, routeState, location }) {
            const urlBase = location.href.split('?')[0];
            const queryString = qsModule.stringify(routeState, {
              addQueryPrefix: true,
              arrayFormat: 'indices',
              // Only encode characters that would break query string parsing.
              // Keys (brackets) and value characters like [ ] spaces are left
              // as-is, producing human-readable URLs.
              encoder: (str, defaultEncoder, charset, type) => {
                if (type === 'key') {
                  return str;
                }
                return String(str).replace(/[%&=+#?]/g, encodeURIComponent);
              },
            });
            return `${urlBase}${queryString}`;
          },
          parseURL({ qsModule, location }) {
            const parsed = qsModule.parse(location.search.slice(1), {
              arrayLimit: 100,
            });
            // Only keep entries whose values are objects (InstantSearch
            // index state). Primitive values such as step=2 originate
            // from other parts of the application and would crash
            // InstantSearch which expects every routeState value to be
            // an object.
            return Object.fromEntries(
              Object.entries(parsed).filter(([, v]) => v !== null && typeof v === 'object')
            );
          },
        }),
        // With the masking on, a custom stateMapping replaces the default
        // `simple()` mapping, which writes the internal collection name and
        // the raw Typesense attribute names straight into the query string.
        // Both are internal naming and are masked here: the URL carries the
        // bare index name and the public filter aliases. With the masking off
        // the default mapping is used, keeping the URL format external
        // scripts parse.
        stateMapping: maskUrls
          ? {
            stateToRoute(uiState) {
              // `configure` is dropped, as the default simple() mapping does:
              // it carries hitsPerPage and the Typesense `filters` string, which
              // are server-side configuration and not user state.
              const { configure, ...indexUiState } = uiState[collectionName] || {};
              return {
                [indexName]: renameAttributes(indexUiState, aliases),
              };
            },
            routeToState(routeState) {
              // The prefixed collection name is also accepted so links shared
              // before the masking still restore their filters.
              const indexRouteState = routeState[indexName] || routeState[collectionName] || {};
              return {
                [collectionName]: renameAttributes(indexRouteState, attributesByAlias),
              };
            },
          }
          : instantsearch.stateMappings.simple(),
      },
    };

    if (options.searchFunction) {
      instantsearchOptions.searchFunction = options.searchFunction;
    }

    return instantsearch(instantsearchOptions);
  }

  /**
   * Sets or clears the pinned_hits parameter for the current search adapter.
   *
   * When a value is provided, the specified documents are pinned at the top of
   * results on every subsequent search request. Passing null removes pinning.
   *
   * @param {string|null} value - Typesense pinned_hits string (e.g.
   *   "entity:node-5:en:1,entity:node-6:en:2"), or null to disable pinning.
   */
  setPinnedHits(value) {
    if (!this.additionalSearchParameters) {
      return;
    }
    if (value) {
      this.additionalSearchParameters.pinned_hits = value;
    } else {
      delete this.additionalSearchParameters.pinned_hits;
    }
  }

  /**
   * Sets or clears the sort_by parameter for the current search adapter.
   *
   * When a value is provided it overrides the sort order for every subsequent
   * search request. Passing null removes the override and falls back to the
   * default sort configured at construction time.
   *
   * @param {string|null} value - Typesense sort_by string (e.g.
   *   "_text_match:desc,field_fm_surname:asc"), or null to remove.
   */
  setSortBy(value) {
    if (!this.additionalSearchParameters) {
      return;
    }
    if (value) {
      this.additionalSearchParameters.sort_by = value;
    } else {
      delete this.additionalSearchParameters.sort_by;
    }
  }

  /**
   * Custom search box connector that includes a loader and clear button.
   *
   * @returns {Function} A connector function for the search box widget.
   */
  getCustomSearchBoxConnector(searchAsYouType = true) {
    return instantsearch.connectors.connectSearchBox((renderOptions, isFirstRender) => {
      const { query, refine, clear, isSearchStalled, widgetParams } = renderOptions;

      if (isFirstRender) {
        const container = document.querySelector(widgetParams.container);
        if (!container) {
          return;
        }

        // Every interpolated value (placeholder, query) is HTML-escaped via
        // Drupal.checkPlain; the rest is a static structural template.
        // eslint-disable-next-line no-unsanitized/property -- all interpolated values escaped with Drupal.checkPlain
        container.innerHTML = `
          <div class="custom-searchbox">
            <input
              type="search"
              class="search-input"
              placeholder="${Drupal.checkPlain(widgetParams.placeholder || 'Search...')}"
              value="${Drupal.checkPlain(query || '')}"
              aria-label="${Drupal.checkPlain(widgetParams.placeholder || 'Search...')}"
            >
            <button class="search-clear" style="display: none;" aria-label="Clear search"></button>
          </div>
        `;

        widgetParams.elements = {
          input: container.querySelector('input'),
          clearBtn: container.querySelector('.search-clear')
        };

        if (!searchAsYouType) {
          widgetParams.elements.input.addEventListener('keydown', event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              refine(event.target.value);
            }
          });
        }
        else {
          widgetParams.elements.input.addEventListener('input', event => {
            refine(event.target.value);
          });
        }

        widgetParams.elements.clearBtn.addEventListener('click', () => {
          clear();
          widgetParams.elements.input.value = '';
          widgetParams.elements.input.focus();
        });
      }

      const { input, clearBtn } = widgetParams.elements;

      clearBtn.style.display = query.length > 0 ? 'block' : 'none';

      if (document.activeElement !== input) {
        input.value = query;
      }
    });
  }

  /**
   * Custom hits connector that displays a message when no results are found.
   *
   * @param {Function} renderHit - A function that takes a hit object and returns the HTML string to render for that hit. Defaults to returning the 'rendered_item' property of the hit.
   * @param {Array<Function>} postSearchEventCallbacks - An array of callback functions to execute after rendering hits, each receiving the current query and search results as arguments. Defaults to an empty array.
   * @param {string} noResultsBlockClass - The BEM block class prefix used for the no-results markup. Defaults to 'lc-internal-search'. Pass 'lc-global-search' for the global search overlay.
   *
   * @returns {Function} A connector function for the hits widget.
   */
  getCustomHitsConnector(
    renderHit = (hit) => hit.rendered_item,
    postSearchEventCallbacks = [],
    noResultsBlockClass = 'lc-internal-search',
  ) {
    return instantsearch.connectors.connectHits((renderOptions, isFirstRender) => {
      const { hits, results, widgetParams } = renderOptions;

      // On first render, cache the container element and show a loading state
      if (isFirstRender) {
        widgetParams.cachedContainer = document.querySelector(widgetParams.container);
        if (!widgetParams.cachedContainer) {
          return;
        }

        widgetParams.cachedContainer.innerHTML = `
          <div class="lc-internal-search__loading">
            <div class="lc-internal-search__loading-spinner">
              <span class="lc-internal-search__loading-spinner-icon"></span>
            </div>
          </div>
        `;

        return;
      }

      const container = widgetParams.cachedContainer;

      // Update the results counter (count only) before the no-hits early return,
      // so an empty result set shows "0 risultati trovati" instead of a stale count.
      if (widgetParams.counterContainerSelector && results && results.nbHits !== undefined) {
        const counterContainer = document.querySelector(widgetParams.counterContainerSelector);
        if (counterContainer) {
          const nbHits = results.nbHits || 0;
          const template = nbHits === 1
            ? (widgetParams.counterSingularLabel || '{nbHits}')
            : (widgetParams.counterPluralLabel || '{nbHits}');
          // The label carries no {query}; the trailing replace is a harmless
          // safety net for any legacy value still holding the placeholder.
          const text = template
            .replace('{nbHits}', nbHits)
            .replace('{query}', '');
          counterContainer.textContent = text;

          // Announce the full counter once per search; pagination re-renders stay
          // silent. The very first render (page load, no user query yet) only
          // seeds the key, so it never announces on its own.
          const announceKey = `${results.query}|${text}`;
          if (!widgetParams.counterInitialized) {
            widgetParams.lastAnnouncedCounter = announceKey;
            widgetParams.counterInitialized = true;
          }
          else if (typeof Drupal.announce === 'function' && announceKey !== widgetParams.lastAnnouncedCounter) {
            Drupal.announce(text, 'polite');
            widgetParams.lastAnnouncedCounter = announceKey;
          }
        }
      }

      // Update small results counter (no query) if container selector is provided.
      if (widgetParams.smallCounterContainerSelector && results && results.nbHits !== undefined) {
        const smallCounterContainer = document.querySelector(widgetParams.smallCounterContainerSelector);
        if (smallCounterContainer) {
          const nbHits = results.nbHits || 0;
          const template = nbHits === 1
            ? (widgetParams.smallCounterSingularLabel || '{nbHits}')
            : (widgetParams.smallCounterPluralLabel || '{nbHits}');
          smallCounterContainer.textContent = template.replace('{nbHits}', nbHits);
        }
      }

      // If no hits, display not found message
      if (hits.length === 0) {
        let nf = widgetParams.notFoundMessages ?? this.notFoundMessages;
        if (typeof nf === 'string') {
          try { nf = JSON.parse(nf); }
          catch (e) { nf = { title: '', description: '' }; }
        }
        const title = nf && nf.title ? nf.title : '';
        const desc = nf && nf.description ? nf.description : '';

        // title/desc are admin-configured notFoundMessages (trusted markup);
        // the user-controlled query is HTML-escaped via Drupal.checkPlain.
        // eslint-disable-next-line no-unsanitized/property -- trusted config markup; the only user input (query) is escaped with Drupal.checkPlain
        container.innerHTML = `
          <div class="${noResultsBlockClass}__no-results">
            <div class="${noResultsBlockClass}__no-results-message">
              <div class="${noResultsBlockClass}__no-results-title">${title}</div>
              <div class="${noResultsBlockClass}__no-results-description">${desc.replace('{query}', Drupal.checkPlain(results && results.query && results.query.trim() !== '*' ? results.query : ''))}</div>
            </div>
          </div>
        `;

        return;
      }

      // Transform each hit's rendered content with dynamic replacements.
      // renderHit returns the server-rendered trusted Drupal markup
      // (hit.rendered_item); injected as-is by design.
      // eslint-disable-next-line no-unsanitized/property -- server-rendered trusted Drupal markup
      container.innerHTML = hits.map(hit => {
        const renderedContent = renderHit(hit);
        let rendered = typeof renderedContent === 'string' ? renderedContent : '';

        if (!rendered) {
          return '';
        }

        // Replace h3 tags with the correct heading level if different from h3
        if (widgetParams.headingLevel && widgetParams.headingLevel !== 'h3') {
          rendered = rendered.replace(/<h3(\s|>)/g, `<${widgetParams.headingLevel}$1`);
          rendered = rendered.replace(/<\/h3>/g, `</${widgetParams.headingLevel}>`);
        }

        const icPlaceholders = widgetParams.icPlaceholders || {};

        if (typeof icPlaceholders === 'object' && icPlaceholders !== null) {
          Object.entries(icPlaceholders).forEach(([key, value]) => {
            const placeholder = `[${key}]`;
            rendered = rendered.split(placeholder).join(value || '');
          });
        }

        return rendered;
      }).join('');

      // A card is rendered once at index time, so a placeholder that resolves
      // to nothing (a degree publishing no academic year) leaves an empty info
      // cell behind. Drop those cells, and the wrapper once it holds none.
      container.querySelectorAll('.lc-programme-list-content__infos-item').forEach(item => {
        if (item.textContent.trim() === '') {
          item.remove();
        }
      });
      container.querySelectorAll('.lc-programme-list-content__infos').forEach(wrapper => {
        if (wrapper.children.length === 0) {
          wrapper.remove();
        }
      });

      // Execute any post-search event callbacks with the current query and results
      if (this.shouldRunCallbacks(results, widgetParams)) {
        for (const callback of postSearchEventCallbacks) {
          if (typeof callback === 'function') {
            try {
              callback(results.query, results);
            }
            catch (e) {
              console.error('Error in post-search event callback:', e);
            }
          }
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Facet refinement helpers – work with both conjunctive and disjunctive facet
  // types so callers don't need to branch on the facet kind.
  // ---------------------------------------------------------------------------

  /**
   * Returns all active refinement values for an attribute, regardless of
   * whether it is a conjunctive or disjunctive facet.
   *
   * @param {object} state - The InstantSearch helper state.
   * @param {string} attr  - The facet attribute name.
   *
   * @returns {string[]} Currently refined values.
   */
  _getRefinements(state, attr) {
    return [
      ...(state.facetsRefinements?.[attr] || []),
      ...(state.disjunctiveFacetsRefinements?.[attr] || []),
    ];
  }

  /**
   * Removes a single refinement value using the correct method for the facet
   * type (conjunctive vs disjunctive).
   *
   * @param {object} state - The InstantSearch helper state.
   * @param {string} attr  - The facet attribute name.
   * @param {string} val   - The value to remove.
   *
   * @returns {object} A new state with the refinement removed.
   */
  _removeFacetRefinement(state, attr, val) {
    if (state.isDisjunctiveFacet?.(attr)) {
      return state.removeDisjunctiveFacetRefinement(attr, val);
    }
    return state.removeFacetRefinement(attr, val);
  }

  /**
   * Adds a single refinement value using the correct method for the facet
   * type (conjunctive vs disjunctive).
   *
   * @param {object} state - The InstantSearch helper state.
   * @param {string} attr  - The facet attribute name.
   * @param {string} val   - The value to add.
   *
   * @returns {object} A new state with the refinement added.
   */
  _addFacetRefinement(state, attr, val) {
    if (state.isDisjunctiveFacet?.(attr)) {
      return state.addDisjunctiveFacetRefinement(attr, val);
    }
    return state.addFacetRefinement(attr, val);
  }

  // ---------------------------------------------------------------------------
  // getCustomMenuSelectConnector – sub-functions
  // ---------------------------------------------------------------------------

  /**
   * Determines whether this select widget should be disabled because its
   * parent dependency has no active refinement.
   *
   * @param {object} widgetParams - The widget configuration.
   * @param {object|null} helper  - The InstantSearch helper instance.
   *
   * @returns {boolean} True when the widget should be disabled.
   */
  _isDependencyDisabled(widgetParams, helper) {
    if (!widgetParams.dependsOn) {
      return false;
    }
    if (helper && helper.state) {
      const facetRefinements = helper.state.facetsRefinements?.[widgetParams.dependsOn] || [];
      const disjunctiveRefinements = helper.state.disjunctiveFacetsRefinements?.[widgetParams.dependsOn] || [];
      return facetRefinements.length === 0 && disjunctiveRefinements.length === 0;
    }
    return true;
  }

  /**
   * When the parent filter is deselected, clears any active refinements on
   * this widget's attribute and triggers a new search.
   *
   * @param {boolean} isDisabled  - Whether the widget is currently disabled.
   * @param {object[]} items      - The connector's live facet items.
   * @param {object} helper       - The InstantSearch helper instance.
   * @param {object} widgetParams - The widget configuration.
   */
  _clearRefinementsIfParentInactive(isDisabled, items, helper, widgetParams) {
    if (!isDisabled || !items.some(item => item.isRefined)) {
      return;
    }
    let state = helper.state;
    const refined = this._getRefinements(state, widgetParams.attribute);
    refined.forEach(val => {
      state = this._removeFacetRefinement(state, widgetParams.attribute, val);
    });
    helper.setState(state.resetPage()).search();
  }

  /**
   * Decides which items the select dropdown should display.
   *
   * Decision tree:
   * 1. Static items (and allowed by view mode) → pre-defined value list
   * 2. facetExcludes configured → async Typesense fetch excluding siblings
   * 3. Default → live connector items (standard faceted behavior)
   *
   * @param {object[]} items               - Live facet items from the connector.
   * @param {object} widgetParams          - The widget configuration.
   * @param {object|null} helper           - The InstantSearch helper.
   * @param {boolean} isDisabled           - Whether the widget is disabled.
   * @param {object} instantSearchInstance - The InstantSearch instance.
   *
   * @returns {object[]} The items to render in the dropdown.
   */
  _resolveDisplayItems(items, widgetParams, helper, isDisabled, instantSearchInstance) {
    const isDependentWithActiveParent = widgetParams.dependsOn && !isDisabled;

    // --- Branch 1: static items ---
    const hasStaticItems = widgetParams.staticItems && widgetParams.staticItems.length > 0;
    const useStaticItems = hasStaticItems
      && !isDependentWithActiveParent
      && (!widgetParams.staticInDefaultViewOnly || this.isDefaultView);

    if (useStaticItems) {
      return this._buildStaticDisplayItems(items, widgetParams, helper);
    }

    // --- Branch 2: dynamic facet fetch (facetExcludes) ---
    const hasFacetExcludes = widgetParams.facetExcludes
      && widgetParams.facetExcludes.length > 0
      && !isDependentWithActiveParent
      && helper && helper.state;

    if (hasFacetExcludes) {
      return this._buildDynamicFacetDisplayItems(items, widgetParams, helper, instantSearchInstance);
    }

    // --- Branch 3: live connector items ---
    return items;
  }

  /**
   * Builds display items from a pre-defined static list, merging live facet
   * data where available.
   *
   * @param {object[]} items       - Live facet items from the connector.
   * @param {object} widgetParams  - The widget configuration (must have staticItems).
   * @param {object|null} helper   - The InstantSearch helper.
   *
   * @returns {object[]} Display items built from the static list.
   */
  _buildStaticDisplayItems(items, widgetParams, helper) {
    const liveMap = new Map(items.map(item => [item.value, item]));
    const activeRefinements = helper && helper.state
      ? new Set(this._getRefinements(helper.state, widgetParams.attribute))
      : new Set();
    return widgetParams.staticItems.map(value => {
      return liveMap.get(value) || { value, label: value, isRefined: activeRefinements.has(value), count: 0 };
    });
  }

  /**
   * Builds display items by fetching facet values from Typesense while
   * excluding refinements on specified sibling attributes.
   *
   * The fetch is async. On first call or while pending, falls back to the
   * live connector items to avoid a blank dropdown.
   *
   * @param {object[]} items               - Live facet items from the connector.
   * @param {object} widgetParams          - The widget configuration.
   * @param {object} helper                - The InstantSearch helper.
   * @param {object} instantSearchInstance - The InstantSearch instance.
   *
   * @returns {object[]} Display items (cached result or live fallback).
   */
  _buildDynamicFacetDisplayItems(items, widgetParams, helper, instantSearchInstance) {
    const stateKey = JSON.stringify({
      refinements: Object.fromEntries(
        [
          ...Object.entries(helper.state.facetsRefinements || {}),
          ...Object.entries(helper.state.disjunctiveFacetsRefinements || {}),
        ]
          .filter(([attr]) => !widgetParams.facetExcludes.includes(attr))
          .filter(([, vals]) => vals.length > 0)
      ),
      query: helper.state.query || '',
    });

    if (widgetParams._dynamicFacetCacheKey !== stateKey) {
      widgetParams._dynamicFacetCacheKey = stateKey;
      widgetParams._dynamicFacetPending = true;
      this.fetchDynamicFacetValues(
        widgetParams.searchIndex,
        widgetParams.attribute,
        widgetParams.facetExcludes,
        helper,
        widgetParams.baseFilters || []
      ).then(values => {
        widgetParams._dynamicFacetCache = values;
        widgetParams._dynamicFacetPending = false;
        instantSearchInstance.scheduleRender();
      }).catch(() => {
        widgetParams._dynamicFacetPending = false;
        widgetParams._dynamicFacetCacheKey = null;
      });
    }

    if (widgetParams._dynamicFacetCache && !widgetParams._dynamicFacetPending) {
      const liveMap = new Map(items.map(item => [item.value, item]));
      const activeRefinements = helper && helper.state
        ? new Set(this._getRefinements(helper.state, widgetParams.attribute))
        : new Set();
      return widgetParams._dynamicFacetCache.map(value => {
        return liveMap.get(value) || { value, label: value, isRefined: activeRefinements.has(value), count: 0 };
      });
    }

    return items;
  }

  /**
   * Returns a label-formatting function. When the widget is configured with
   * valueLabels, a matching raw value is replaced by its mapped label first.
   * Otherwise, when configured with monthNameFormat, numeric month values
   * ("01"–"12") are converted to localized month names. Failing both, the raw
   * value is returned as-is.
   *
   * The Intl.DateTimeFormat formatter is created once and cached on
   * widgetParams to avoid re-instantiation on every render.
   *
   * @param {object} widgetParams - The widget configuration.
   *
   * @returns {Function} A function (value: string) => string.
   */
  _createLabelFormatter(widgetParams) {
    if (!widgetParams.monthFormatter && widgetParams.monthNameFormat) {
      widgetParams.monthFormatter = new Intl.DateTimeFormat(
        document.documentElement.lang || 'en',
        { month: widgetParams.monthNameFormat }
      );
    }
    return (value) => {
      // Explicit value→label overrides (e.g. the in-press sentinel "9999"
      // mapped to an Instance Configurator label). The facet value is left
      // untouched so refinement still filters by the raw value.
      if (widgetParams.valueLabels && Object.prototype.hasOwnProperty.call(widgetParams.valueLabels, value)) {
        return widgetParams.valueLabels[value];
      }
      if (!widgetParams.monthFormatter) {
        return value;
      }
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 1 || num > 12) {
        return value;
      }
      const monthName = widgetParams.monthFormatter.format(new Date(2000, num - 1, 1));
      return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    };
  }

  /**
   * Renders the <select> dropdown HTML into the container.
   *
   * @param {HTMLElement} container - The DOM element to render into.
   * @param {object} widgetParams  - The widget configuration.
   * @param {object[]} displayItems - The items to render as <option> elements.
   * @param {boolean} isDisabled   - Whether the widget is disabled.
   * @param {Function} getLabel    - Label formatting function.
   */
  _renderSelectHTML(container, widgetParams, displayItems, isDisabled, getLabel) {
    // Every interpolated value (label, placeholder, option value/label) is
    // HTML-escaped via Drupal.checkPlain; disabled/selected are static flags.
    // eslint-disable-next-line no-unsanitized/property -- all interpolated values escaped with Drupal.checkPlain
    container.innerHTML = `
      <span class="lc-internal-search__select-label">${Drupal.checkPlain(widgetParams.label || '')}</span>
      <select
        id="${widgetParams.container.replace('#', '')}--select"
        class="lc-internal-search__select-container ${isDisabled ? 'is-disabled' : ''}"
        aria-label="${Drupal.checkPlain(widgetParams.label || '')}"
        aria-disabled="${isDisabled ? 'true' : 'false'}"
        ${isDisabled ? 'disabled' : ''}
      >
        <option value="">${Drupal.checkPlain(widgetParams.placeholder || '')}</option>
        ${displayItems
      .map(item => `
            <option value="${Drupal.checkPlain(item.value || '')}" ${item.isRefined ? 'selected' : ''}>
              ${Drupal.checkPlain(getLabel(item.label) || '')}
            </option>
          `)
      .join('')}
      </select>
    `;
  }

  /**
   * Binds the change event on the rendered <select> element, implementing
   * atomic single-select refinement and dependent attribute clearing.
   *
   * @param {HTMLElement} container - The DOM element containing the <select>.
   * @param {object} widgetParams  - The widget configuration.
   * @param {object} helper        - The InstantSearch helper instance.
   * @param {boolean} isDisabled   - Whether the widget is disabled.
   */
  _bindSelectChangeHandler(container, widgetParams, helper, isDisabled) {
    const selectEl = container.querySelector('select');
    if (!selectEl) {
      return;
    }

    selectEl.addEventListener('change', event => {
      if (isDisabled) {
        event.preventDefault();
        selectEl.value = '';
        return;
      }

      // Atomic single-select: clear current refinement and set the new
      // value in one state transition so only one search fires.
      let state = helper.state;
      const currentRefined = this._getRefinements(state, widgetParams.attribute);
      currentRefined.forEach(val => {
        state = this._removeFacetRefinement(state, widgetParams.attribute, val);
      });
      if (event.target.value) {
        state = this._addFacetRefinement(state, widgetParams.attribute, event.target.value);
      }

      // Clear any dependent filters when the parent value changes.
      if (widgetParams.dependentAttributes && widgetParams.dependentAttributes.length > 0) {
        widgetParams.dependentAttributes.forEach(depAttr => {
          const depRefined = this._getRefinements(state, depAttr);
          depRefined.forEach(val => {
            state = this._removeFacetRefinement(state, depAttr, val);
          });
        });
      }

      helper.setState(state.resetPage()).search();
    });
  }

  // ---------------------------------------------------------------------------
  // getCustomMenuSelectConnector – main entry point
  // ---------------------------------------------------------------------------

  /**
   * Custom select connector that renders a dropdown using disjunctive facets
   * (connectRefinementList with operator 'or'). Disjunctive facets ensure
   * a filter never hides its own sibling values when one is selected.
   *
   * Callers must pass `operator: 'or'` and a suitable `limit` (e.g. 50) in
   * widgetParams.
   *
   * @returns {Function} A connector function for the select widget.
   */
  getCustomMenuSelectConnector() {
    return instantsearch.connectors.connectRefinementList((renderOptions, isFirstRender) => {
      const { items, widgetParams, instantSearchInstance } = renderOptions;

      if (isFirstRender) {
        widgetParams.cachedContainer = document.querySelector(widgetParams.container);
      }

      const container = widgetParams.cachedContainer;
      if (!container) {
        return;
      }

      const helper = instantSearchInstance.helper;
      const isDisabled = this._isDependencyDisabled(widgetParams, helper);

      // Clear child refinements when the parent is deselected.
      this._clearRefinementsIfParentInactive(isDisabled, items, helper, widgetParams);

      // Resolve which items to show in the dropdown.
      const displayItems = this._resolveDisplayItems(items, widgetParams, helper, isDisabled, instantSearchInstance);

      // Build the label formatter (handles month-name transforms).
      const getLabel = this._createLabelFormatter(widgetParams);

      // Render the <select> HTML.
      this._renderSelectHTML(container, widgetParams, displayItems, isDisabled, getLabel);

      // Bind the change handler.
      this._bindSelectChangeHandler(container, widgetParams, helper, isDisabled);
    });
  }

  /**
   * Fetches all possible values for a facet attribute from Typesense without
   * any active filters. Returns a sorted array of string values.
   *
   * Use this to pre-populate a filter select with options that exist in the
   * index but would normally be hidden because the current default_filter_by
   * excludes them from the live result set.
   *
   * The call uses per_page=0 so Typesense returns zero document bodies — only
   * facet counts. This makes it very fast and the payload tiny. Results are
   * also covered by the Typesense server-side search cache.
   *
   * @param {string} indexName - The Typesense collection name (without prefix).
   * @param {string} attribute - The facet attribute to retrieve values for.
   * @param {string[]} [baseFilters=[]] - Base filter_by strings (e.g. langcode) to
   *   scope results to the current language/context, matching the filters applied
   *   by the main search. Without these, values from all languages would bleed in.
   *
   * @returns {Promise<string[]>} Sorted array of all facet values.
   */
  async prefetchFacetValues(indexName, attribute, baseFilters = []) {
    const collection = this.credentials.collection_prefix + indexName;
    // Use the first configured query_by field to satisfy Typesense's
    // requirement for a query_by parameter when q=*.
    const firstQueryField = this.queryBy.split(',')[0].trim();
    const base = `${this.credentials.protocol}://${this.credentials.host}:${this.credentials.port}`;
    const params = new URLSearchParams({
      q: '*',
      query_by: firstQueryField,
      per_page: '0',
      facet_by: attribute,
      use_cache: 'true',
    });
    if (baseFilters.length > 0) {
      params.set('filter_by', baseFilters.join(' && '));
    }
    const response = await fetch(`${base}/collections/${collection}/documents/search?${params}`, {
      headers: { 'X-TYPESENSE-API-KEY': this.credentials.apiKey },
    });
    if (!response.ok) {
      throw new Error(`Typesense prefetch failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const facet = (data.facet_counts || []).find(f => f.field_name === attribute);
    return facet ? facet.counts.map(c => c.value).sort() : [];
  }

  /**
   * Fetches facet values for a given attribute applying all current filters
   * EXCEPT the refinements on specified excluded attributes.
   *
   * Used to compute year dropdown options without the month refinement
   * affecting them, while still respecting all other active filters
   * (category, query, base filters).
   *
   * @param {string} indexName - The Typesense collection name (without prefix).
   * @param {string} attribute - The facet attribute to retrieve values for.
   * @param {string[]} excludeAttributes - Attributes whose refinements are excluded.
   * @param {object} helper - The InstantSearch helper instance.
   * @param {string[]} baseFilters - Base filter_by strings (langcode, filter_by).
   *
   * @returns {Promise<string[]>} Sorted array of facet values.
   */
  async fetchDynamicFacetValues(indexName, attribute, excludeAttributes, helper, baseFilters) {
    const collection = this.credentials.collection_prefix + indexName;
    const firstQueryField = this.queryBy.split(',')[0].trim();
    const q = helper.state.query?.trim() || '*';

    const filters = [...baseFilters];
    const allRefinements = {
      ...(helper.state.facetsRefinements || {}),
      ...(helper.state.disjunctiveFacetsRefinements || {}),
    };
    for (const [attr, values] of Object.entries(allRefinements)) {
      if (values.length > 0 && !excludeAttributes.includes(attr) && attr !== attribute) {
        // Strip backtick characters to prevent filter injection via crafted URL values.
        const escaped = values.map(v => `\`${String(v).replace(/`/g, '')}\``).join(',');
        filters.push(`${attr}:=[${escaped}]`);
      }
    }

    const base = `${this.credentials.protocol}://${this.credentials.host}:${this.credentials.port}`;
    const params = new URLSearchParams({
      q,
      query_by: firstQueryField,
      per_page: '0',
      facet_by: attribute,
      use_cache: 'true',
    });
    if (filters.length > 0) {
      params.set('filter_by', filters.join(' && '));
    }

    const response = await fetch(`${base}/collections/${collection}/documents/search?${params}`, {
      headers: { 'X-TYPESENSE-API-KEY': this.credentials.apiKey },
    });
    if (!response.ok) {
      throw new Error(`Typesense dynamic facet fetch failed: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    const facet = (data.facet_counts || []).find(f => f.field_name === attribute);
    return facet ? facet.counts.map(c => c.value).sort() : [];
  }

  /**
   * Custom toggle refinement connector that renders a single checkbox.
   *
   * Intended for boolean facet fields. When checked, it refines results to
   * records where the attribute matches the configured `on` value (e.g. 'true').
   * When unchecked, the refinement is removed.
   *
   * @returns {Function} A connector function for the toggle refinement widget.
   */
  getCustomToggleRefinementConnector() {
    return instantsearch.connectors.connectToggleRefinement((renderOptions, isFirstRender) => {
      const { value, refine, widgetParams } = renderOptions;

      if (isFirstRender) {
        widgetParams.cachedContainer = document.querySelector(widgetParams.container);
      }

      const container = widgetParams.cachedContainer;
      if (!container) {
        return;
      }

      const checkboxId = `${widgetParams.container.replace(/[^a-zA-Z0-9-_]/g, '')}--checkbox`;

      // checkboxId is stripped to [a-zA-Z0-9-_] and the label is HTML-escaped
      // via Drupal.checkPlain; checked is a static flag.
      // eslint-disable-next-line no-unsanitized/property -- checkboxId sanitized to an id-safe charset, label escaped with Drupal.checkPlain
      container.innerHTML = `
        <label class="lc-internal-search__checkbox-label" for="${checkboxId}">
          <input
            type="checkbox"
            id="${checkboxId}"
            class="lc-internal-search__checkbox-input"
            ${value.isRefined ? 'checked' : ''}
          >
          <span class="lc-internal-search__checkbox-text">${Drupal.checkPlain(widgetParams.label || '')}</span>
        </label>
      `;

      container.querySelector('input').addEventListener('change', () => {
        refine(value);
      });

      // Show or hide the target filter group based on the checkbox state.
      if (widgetParams.toggleTarget) {
        const targetEl = document.querySelector(
          `[data-filters-toggle-id="${widgetParams.toggleTarget}"]`
        );
        if (targetEl) {
          targetEl.classList.toggle('lc-internal-search__filter-group--hidden', !value.isRefined);
        }
      }
    });
  }

  /**
   * Custom pagination connector that renders pagination controls.
   *
   * @returns {Function} A connector function for the pagination widget.
   */
  getCustomPaginationConnector() {
    return instantsearch.connectors.connectPagination((renderOptions, isFirstRender) => {
      if (this.pagination === null) {
        return;
      }

      const {
        pages, currentRefinement, nbPages, isFirstPage, isLastPage, refine, widgetParams,
      } = renderOptions;

      if (isFirstRender) {
        widgetParams.cachedContainer = document.querySelector(widgetParams.container);
      }

      const container = widgetParams.cachedContainer;
      if (!container) {
        return;
      }

      const classPrefix = container.dataset.pagerClassPrefix || 'lc-pager';

      if (nbPages <= 1) {
        container.innerHTML = '';
        return;
      }

      const hitsPerPage = widgetParams.hitsPerPage || 12;
      const firstItem = currentRefinement * hitsPerPage + 1;
      const lastItem = Math.min((currentRefinement + 1) * hitsPerPage, renderOptions.nbHits);

      let statusMessage = '';
      if (this.pagination && this.pagination.statusMessage) {
        statusMessage = this.pagination.statusMessage
          .replace('{firstItem}', firstItem)
          .replace('{lastItem}', lastItem)
          .replace('{totalItems}', renderOptions.nbHits);
      }

      const defaultPrevIcon = `<svg class="${classPrefix}__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      const defaultNextIcon = `<svg class="${classPrefix}__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

      const prevButtonIcon = this.pagination && this.pagination.prevButtonIcon ? this.pagination.prevButtonIcon : defaultPrevIcon;
      const nextButtonIcon = this.pagination && this.pagination.nextButtonIcon ? this.pagination.nextButtonIcon : defaultNextIcon;

      // classPrefix comes from a data attribute, the icons/labels/statusMessage
      // from admin-configured pagination config, and every interpolated index
      // (page, currentRefinement) is numeric — no user-controlled string
      // reaches the markup.
      // eslint-disable-next-line no-unsanitized/property -- classPrefix from data attribute, icons/labels from trusted config, indices are numeric
      container.innerHTML = `
        <div class="${classPrefix}">
          ${statusMessage ? `<div class="${classPrefix}__status">${statusMessage}</div>` : ''}
          <div class="${classPrefix}__controls">
            <button type="button" class="${classPrefix}__button ${classPrefix}__button--prev ${isFirstPage ? 'disabled' : ''}" data-value="${currentRefinement - 1}" ${isFirstPage ? 'disabled' : ''}>
              <span class="visually-hidden">${(this.pagination && this.pagination.prevButtonLabel) || 'Previous page'}</span>
              ${prevButtonIcon}
            </button>
            <div class="${classPrefix}__numbers">
              ${pages.map(page => `
                <button type="button" class="${classPrefix}__button ${classPrefix}__button--number ${currentRefinement === page ? 'is-active' : ''}" data-value="${page}" ${currentRefinement === page ? 'aria-current="page"' : ''}>
                  <span class="visually-hidden">${(this.pagination && this.pagination.pageButtonLabel) || 'Go to page '}</span><span class="${classPrefix}__button-value">${page + 1}</span>
                </button>
              `).join('')}
            </div>
            <button type="button" class="${classPrefix}__button ${classPrefix}__button--next ${isLastPage ? 'disabled' : ''}" data-value="${currentRefinement + 1}" ${isLastPage ? 'disabled' : ''}>
              <span class="visually-hidden">${(this.pagination && this.pagination.nextButtonLabel) || 'Next page'}</span>
              ${nextButtonIcon}
            </button>
          </div>
        </div>
      `;

      container.querySelectorAll(`.${classPrefix}__button`).forEach(button => {
        button.addEventListener('click', event => {
          event.preventDefault();
          const page = parseInt(event.currentTarget.dataset.value, 10);
          if (page >= 0 && page < nbPages) {
            const scrollTarget = widgetParams.scrollTo
              ? document.querySelector(widgetParams.scrollTo)
              : null;

            // Observe so the scroll fires only after InstantSearch has written the new results into the DOM.
            if (scrollTarget) {
              const observer = new MutationObserver(() => {
                observer.disconnect();
                scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
              });
              observer.observe(scrollTarget, { childList: true });
            }

            refine(page);

            if (!scrollTarget) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }
        });
      });
    });
  }

}

export default LuissSearch;
